import {
  Dropdown,
  FluentProvider,
  Input,
  makeStyles,
} from '@fluentui/react-components';
import { useActiveTheme } from '../../theme/ThemeContext';
import { getFieldPanelContext } from '@moko/utils';
import { useOtherInfoContext } from '../OtherInfoContext';
import { LookupOptions } from '../LookupOptions';
import { isFieldReadOnly } from '../fieldReadOnly';
import { FieldWrapper } from './FieldWrapper';
import type { FieldComponentProps } from './types';

const useStyles = makeStyles({
  unitDropdown: {
    minWidth: 0,
    flex: 1,
  },
});

interface NumberWithUnitFieldProps extends FieldComponentProps {
  metadata: any;
  allOptionsObj: any;
}

export const NumberWithUnitField = ({
  field,
  fieldValue,
  isFieldDisabled,
  isSaved,
  validationMessage,
  metadata,
  allOptionsObj,
}: NumberWithUnitFieldProps) => {
  const styles = useStyles();
  const theme = useActiveTheme();
  const {
    handleOtherInfoChange,
    validateField,
    otherInfoObj,
    getRepeatingPanelIndex,
    currentElementInEntityRepeatingPanel,
    params,
  } = useOtherInfoContext();

  const unitFieldName = `${field.name}_Unit`;
  // _Unit is a companion value stored alongside the main field, not a separate metadata field.
  // Use the main field's panel context to resolve it correctly in repeating panels.
  const { fieldParentName, manualRepeating, entityRepeating } =
    getFieldPanelContext(field.name, metadata.fields);
  let unitValue: any;
  if (fieldParentName && manualRepeating) {
    const index = getRepeatingPanelIndex(fieldParentName);
    unitValue = otherInfoObj[fieldParentName]?.[index]?.[unitFieldName];
  } else if (fieldParentName && entityRepeating) {
    const entityIndex = otherInfoObj[fieldParentName]?.findIndex(
      (entity: any) => entity.id === currentElementInEntityRepeatingPanel
    );
    unitValue =
      entityIndex >= 0
        ? otherInfoObj[fieldParentName]?.[entityIndex]?.[unitFieldName]
        : undefined;
  } else {
    unitValue = otherInfoObj[unitFieldName];
  }

  const unitMessage = (params.validationErrors ?? []).find(
    (error) => error.fieldName === unitFieldName
  )?.message;

  return (
    <FieldWrapper
      field={field}
      validationMessage={validationMessage || unitMessage}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          minWidth: 0,
          gap: '5px',
        }}
      >
        <Input
          id={field.name}
          style={{ flex: 1, minWidth: 0 }}
          onChange={(_, newValue) => {
            handleOtherInfoChange(field.name, newValue.value);
            validateField(field.name, newValue.value);
          }}
          type="number"
          value={fieldValue == null ? '' : String(fieldValue)}
          readOnly={isSaved || isFieldReadOnly(field)}
          disabled={isFieldDisabled}
        />
        <FluentProvider theme={theme} style={{ flex: 1, minWidth: 0 }}>
          <Dropdown
            className={styles.unitDropdown}
            style={{ minWidth: 'auto', width: '100%' }}
            value={
              allOptionsObj.current?.[`${field.lookupList}_${unitValue}`] ?? ''
            }
            onOptionSelect={(_, option) => {
              const newUnitValue = option?.optionValue?.toString();
              handleOtherInfoChange(unitFieldName, newUnitValue);
              validateField(unitFieldName, newUnitValue);
            }}
            disabled={isFieldDisabled}
          >
            <LookupOptions metadata={metadata} field={field} />
          </Dropdown>
        </FluentProvider>
      </div>
    </FieldWrapper>
  );
};
